import * as THREE from 'three';

export const FILTER_KEYWORDS = ['shader', 'shadow', 'ground', 'helper'];

export const HIGHLIGHT_COLOR = new THREE.Color(0x7BD6E0);

export function extractInternals(mv) {
  let scene = null, camera = null;
  const symbols = Object.getOwnPropertySymbols(mv);
  for (const sym of symbols) {
    const val = mv[sym];
    if (val && val.isScene) scene = val;
    if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
    if (val && val.scene && val.scene.isScene) scene = val.scene;
    if (val && val.camera && (val.camera.isCamera || val.camera.isPerspectiveCamera)) camera = val.camera;
  }
  if (!scene) {
    const keys = Object.getOwnPropertyNames(mv);
    for (const key of keys) {
      try {
        const val = mv[key];
        if (val && val.isScene) scene = val;
        if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
      } catch (e) {}
    }
  }
  if (scene && !camera) {
    scene.traverse((obj) => {
      if (obj.isCamera || obj.isPerspectiveCamera) camera = obj;
    });
  }
  return { scene, camera };
}

export function extractScene(mv) {
  return extractInternals(mv).scene;
}

export function normalizeName(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/\.[0-9]+$|\b[0-9]{3}$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isFiltered(name, matName) {
  const low = (name || '').toLowerCase();
  const lowMat = (matName || '').toLowerCase();
  return FILTER_KEYWORDS.some((f) => low.includes(f) || lowMat.includes(f));
}

export function findMeshByName(scene, targetRaw) {
  const target = normalizeName(targetRaw);
  if (!target) { console.log('[findMeshByName] empty target'); return null; }

  let best = null;
  scene.traverse((obj) => {
    if (!obj.isMesh || best) return;
    if (isFiltered(obj.name, obj.material?.name)) return;
    if (normalizeName(obj.name) === target || normalizeName(obj.material?.name) === target) {
      best = obj;
    }
  });
  if (best) {
    console.log('[findMeshByName] exact match:', best.name, 'for target:', target);
    return best;
  }

  const candidates = [];
  scene.traverse((obj) => {
    if (!obj.isMesh) return;
    if (isFiltered(obj.name, obj.material?.name)) return;
    const nn = normalizeName(obj.name);
    if (nn && (nn.includes(target) || target.includes(nn))) {
      candidates.push({ obj, diff: Math.abs(nn.length - target.length) });
    }
  });
  if (candidates.length > 0) {
    candidates.sort((a, b) => a.diff - b.diff);
    best = candidates[0].obj;
  }
  if (best) {
    console.log('[findMeshByName] fuzzy match:', best.name, 'for target:', target);
  } else {
    console.log('[findMeshByName] NO match found for target:', target);
    // Log a few mesh names to help debug
    let count = 0;
    scene.traverse((obj) => {
      if (obj.isMesh && count < 5) {
        console.log('[findMeshByName] available mesh:', obj.name, 'normalized:', normalizeName(obj.name));
        count++;
      }
    });
  }
  return best;
}

export function focusCameraOn(mv, center, size) {
  const radius = Math.max(size.x, size.y, size.z) / 2;
  const distance = Math.max(radius * 2.6, 0.4);
  const targetStr = `${center.x.toFixed(4)} ${center.y.toFixed(4)} ${center.z.toFixed(4)}`;
  const orbitStr = `0deg 85deg ${distance.toFixed(3)}m`;

  const jump = () => {
    try {
      mv.cameraTarget = targetStr;
      mv.cameraOrbit = orbitStr;
      if (mv.jumpCameraToGoal) mv.jumpCameraToGoal();
    } catch (e) {}
  };

  jump();
  requestAnimationFrame(jump);

  // Direct manipulation of internal Three.js camera + controls
  requestAnimationFrame(() => {
    try {
      const internals = extractInternals(mv);
      const cam = internals.camera;
      if (!cam) return;

      const offset = new THREE.Vector3(0, 0, distance);
      cam.position.copy(center).add(offset);
      cam.lookAt(center);

      const symbols = Object.getOwnPropertySymbols(mv);
      for (const sym of symbols) {
        const val = mv[sym];
        if (val && val.target && typeof val.update === 'function') {
          val.target.copy(center);
          val.update();
        }
      }

      mv.requestUpdate?.();
    } catch (e) {}
  });

  // Monitor camera every frame for ~6s and re-apply only if it deviates
  let monitorFrames = 0;
  const isDeviated = () => {
    try {
      const cur = mv.cameraTarget;
      if (!cur) return true;
      const parts = cur.replace(/[()]/g, '').split(/\s+/).map(parseFloat);
      if (parts.length < 3) return true;
      return Math.abs(parts[0] - center.x) > 0.01
          || Math.abs(parts[1] - center.y) > 0.01
          || Math.abs(parts[2] - center.z) > 0.01;
    } catch (e) { return true; }
  };

  const monitorLoop = () => {
    if (monitorFrames++ > 360) return;
    if (isDeviated()) jump();
    requestAnimationFrame(monitorLoop);
  };
  setTimeout(() => { monitorFrames = 0; requestAnimationFrame(monitorLoop); }, 200);
}

export function forceRender(mv) {
  if (!mv) return;
  if (mv.requestUpdate) mv.requestUpdate();
  try {
    const expAttr = mv.getAttribute('exposure') || '1';
    const exp = parseFloat(expAttr);
    mv.setAttribute('exposure', (exp + 0.000001).toString());
    requestAnimationFrame(() => {
      if (mv) mv.setAttribute('exposure', expAttr);
    });
  } catch (e) {}
}
